/**
 * Remembers which concluded partnerships have already popped the coach
 * feedback modal open, so the dashboard prompts at most once per browser
 * session per partnership rather than on every visit.
 *
 * `sessionStorage` rather than `localStorage`: a coachee who dismissed the
 * prompt should see it again next time they come back, just not every time
 * they return to the dashboard in the same sitting. The banner stays either
 * way, so they can always open the modal themselves.
 *
 * Storage can throw (private mode, blocked site data) — every access is
 * wrapped, and an in-memory record backs it up so the prompt still only
 * opens once per page load when storage is unavailable.
 */

const PROMPTED_STORAGE_PREFIX = 'll-coach-feedback-prompted';

const promptedThisPageLoad = new Set<string>();

export function feedbackPromptStorageKey(partnershipId: string): string {
  return `${PROMPTED_STORAGE_PREFIX}:${partnershipId}`;
}

function promptedThisSession(partnershipId: string): boolean {
  try {
    return (
      window.sessionStorage.getItem(feedbackPromptStorageKey(partnershipId)) !==
      null
    );
  } catch {
    return false;
  }
}

/**
 * Whether some other modal is already on screen. Popping a second dialog on
 * top of it would steal focus from whatever the coachee was doing.
 */
function anotherModalIsOpen(): boolean {
  return document.querySelector('.modal.show, [aria-modal="true"]') !== null;
}

/** Whether the feedback modal should open by itself for this partnership. */
export function shouldAutoPromptForFeedback(partnershipId: string): boolean {
  if (promptedThisPageLoad.has(partnershipId)) {
    return false;
  }

  return !promptedThisSession(partnershipId) && !anotherModalIsOpen();
}

/** Records that the feedback modal has been shown for this partnership. */
export function markPromptedForFeedback(partnershipId: string): void {
  promptedThisPageLoad.add(partnershipId);
  try {
    window.sessionStorage.setItem(feedbackPromptStorageKey(partnershipId), '1');
  } catch {
    // Storage unavailable: the in-memory record still covers this page load.
  }
}
